"use client";

import React, { useState, useEffect, useId, useMemo } from "react";

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

export interface ContainerTextFlipProps {
  /** Array of words to cycle through in the animation */
  words?: string[];
  /** Time in milliseconds between word transitions */
  interval?: number;
  /** Additional CSS classes to apply to the container */
  className?: string;
  /** Additional CSS classes to apply to the text */
  textClassName?: string;
  /** Duration of the transition animation in milliseconds */
  animationDuration?: number;
}

export function ContainerTextFlip({
  words = ["better", "modern", "beautiful", "awesome"],
  interval = 3000,
  className,
  textClassName,
  animationDuration = 700,
}: ContainerTextFlipProps) {
  const id = useId();
  const prefersReducedMotion = useReducedMotion();
  const wordsKey = words.join("\0");
  const safeWords = useMemo(() => (wordsKey.length > 0 ? wordsKey.split("\0") : [""]), [wordsKey]);
  const effectiveWords = useMemo(
    () => (prefersReducedMotion ? [safeWords[0]] : safeWords),
    [prefersReducedMotion, safeWords]
  );
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [width, setWidth] = useState(100);
  const textRef = React.useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    setCurrentWordIndex(0);
  }, [prefersReducedMotion]);

  const updateWidthForWord = () => {
    if (textRef.current) {
      const textWidth = textRef.current.scrollWidth + 30;
      setWidth(textWidth);
    }
  };

  useEffect(() => {
    updateWidthForWord();
  }, [currentWordIndex, effectiveWords]);

  useEffect(() => {
    if (prefersReducedMotion || effectiveWords.length < 2) return;
    const intervalId = setInterval(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % effectiveWords.length);
    }, interval);

    return () => clearInterval(intervalId);
  }, [effectiveWords, interval, prefersReducedMotion]);

  return (
    <motion.span
      layout
      layoutId={`words-here-${id}`}
      animate={{ width }}
      transition={{ duration: animationDuration / 2000 }}
      className={cn(
        "relative inline-flex items-center justify-center rounded-xl px-4 py-2 text-center text-4xl font-bold text-neutral-800 md:text-7xl dark:text-white",
        "[background:linear-gradient(to_bottom,#f5f5f5,#e5e5e5)]",
        "shadow-[inset_0_-1px_#d4d4d4,inset_0_0_0_1px_#d4d4d4,_0_4px_12px_rgba(0,0,0,0.1)]",
        "dark:[background:linear-gradient(to_bottom,#262626,#171717)]",
        "dark:shadow-[inset_0_-1px_rgba(255,255,255,0.05),inset_0_0_0_1px_rgba(255,255,255,0.08),_0_4px_12px_rgba(0,0,0,0.4)]",
        className,
      )}
      key={effectiveWords[currentWordIndex]}
    >
      <motion.span
        transition={{
          duration: animationDuration / 1000,
          ease: "easeInOut",
        }}
        className={cn("inline-block", textClassName)}
        ref={textRef}
        layoutId={`word-div-${effectiveWords[currentWordIndex]}-${id}`}
      >
        <motion.span className="inline-block">
          {prefersReducedMotion
            ? effectiveWords[currentWordIndex]
            : effectiveWords[currentWordIndex].split("").map((letter, index) => (
                <motion.span
                  key={index}
                  initial={{
                    opacity: 0,
                    filter: "blur(10px)",
                  }}
                  animate={{
                    opacity: 1,
                    filter: "blur(0px)",
                  }}
                  transition={{
                    delay: index * 0.02,
                  }}
                >
                  {letter}
                </motion.span>
              ))}
        </motion.span>
      </motion.span>
    </motion.span>
  );
}
